import React, { forwardRef, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/ja'
import type { Dayjs } from 'dayjs'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { RADIUS } from '@/shared/styles/radii'

// Notion 流 date picker.
// floating label を撤去し、 label を上に 13/500 で別 mount.
// input chrome は NotionTextField と同じ chip-shape.

interface NotionDatePickerProps
  extends Omit<DatePickerProps<Dayjs>, 'label' | 'slotProps'> {
  label?: ReactNode
  placeholder?: string
  fullWidth?: boolean
}

const inputSx = {
  fontSize: 14,
  color: colorTokens.fg.default,
  backgroundColor: colorTokens.bg.default,
  borderRadius: `${RADIUS.md}px`,
  '& fieldset': {
    borderColor: colorTokens.border.subtle,
    transition: 'border-color 120ms ease',
  },
  '&:hover fieldset': {
    borderColor: `${colorTokens.border.default} !important`,
  },
  '&.Mui-focused fieldset': {
    borderColor: `${colorTokens.accent.primary} !important`,
    borderWidth: '1px !important',
  },
  '& input': {
    py: 1,
    px: 1.5,
    '&::placeholder': {
      color: colorTokens.fg.faint,
      opacity: 1,
    },
  },
}

export const NotionDatePicker = forwardRef<
  HTMLDivElement,
  NotionDatePickerProps
>(({ label, placeholder, fullWidth = true, sx, ...rest }, ref) => (
  <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {label && (
      <Typography component="label" sx={PORTAL_TEXT.caption}>
        {label}
      </Typography>
    )}
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
      <DatePicker
        slotProps={{
          textField: {
            size: 'small',
            fullWidth,
            placeholder: placeholder ?? '',
            InputProps: { sx: inputSx },
          },
        }}
        sx={sx}
        {...rest}
      />
    </LocalizationProvider>
  </Box>
))
NotionDatePicker.displayName = 'NotionDatePicker'
